import { Color3 } from "@babylonjs/core/Maths/math.color.pure.js";
import {
  SKULL_GARDENS, SKULL_ISLE, SKULL_ORIGIN, SKULL_PALMS, SKULL_PATHS, SKULL_SOLIDS, SKULL_TERRACES, SKULL_TOWERS,
  clamp, hash2, type Collider, type SkullPalm,
} from "@tiny-seas/shared";
import type { SurfaceFamily } from "./materials.ts";
import { terrainSurfaceHeight } from "./terrain-surface.ts";

/**
 * Skull's static salvage fort as plain vertex data: one buffer set per 40 m region,
 * surface family and shadow role. Pure (maths-only Babylon import), so the bake
 * worker builds it off the main thread; `SkullScene` turns the parts into meshes.
 */

type Point = readonly [number, number, number];
type UV = readonly [number, number];
const WOOD = ["#927953", "#9c825c", "#a38a64", "#88714e", "#97805a"] as const;
const LEAF = ["#52743c", "#63834a", "#708b50", "#486938", "#799453"] as const;
const ROCK = ["#c3a171", "#b99a6c", "#cba979", "#b69a70", "#c6a575"] as const;
const lerp = (a: Point, b: Point, t: number): Point => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

/** Fixture centres are also Environment's point-light anchors; all coordinates are world metres. */
export const SKULL_LIGHT_ANCHORS = [
  { id: "lower-gate-west", x: -33, y: 11.35, z: -74.55, mountX: -33, mountY: 12.27, mountZ: -74.2 },
  { id: "lower-gate-east", x: -27, y: 11.35, z: -74.55, mountX: -27, mountY: 12.27, mountZ: -74.2 },
  { id: "crossing-west", x: -40, y: 21.1, z: -10.95, mountX: -40, mountY: 21.15, mountZ: -10.31 },
  { id: "crossing-east", x: -10, y: 21.1, z: -10.95, mountX: -10, mountY: 21.15, mountZ: -10.31 },
  { id: "underbridge-stores", x: -29.3, y: 10.87, z: 4, mountX: -29.3, mountY: 11.8, mountZ: 4 },
  { id: "upper-gate-west", x: 20.7, y: 37.5, z: 16.2, mountX: 20.7, mountY: 38.42, mountZ: 16.8 },
  { id: "upper-gate-east", x: 37.3, y: 37.5, z: 16.2, mountX: 37.3, mountY: 38.42, mountZ: 16.8 },
  { id: "mast-yard", x: 10, y: 45.1, z: 36, mountX: 10, mountY: 46.03, mountZ: 36 },
  { id: "offshore-watch", x: 83.55, y: 17.3, z: -56.7, mountX: 82.7, mountY: 18.22, mountZ: -56.7 },
].map(anchor => ({ ...anchor, x: anchor.x + SKULL_ORIGIN.x, z: anchor.z + SKULL_ORIGIN.z,
  mountX: anchor.mountX + SKULL_ORIGIN.x, mountZ: anchor.mountZ + SKULL_ORIGIN.z }));

/** Direct regional vertex buffers: no temporary mesh per log, leaf, plank or lashing. */
class FortBatch {
  readonly positions: number[] = [];
  readonly normals: number[] = [];
  readonly colors: number[] = [];
  readonly uvs: number[] = [];
  readonly indices: number[] = [];
  private readonly tints = new Map<string, Color3>();
  /** `metres`: the family's texture repeat (SURFACE_PROFILES). */
  constructor(readonly family: SurfaceFamily, readonly shadows: boolean, private readonly metres: number) {}

  triangle(a: Point, b: Point, c: Point, hex: string, uv?: readonly [UV, UV, UV]): void {
    const base = this.positions.length / 3;
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
    const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2];
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const length = Math.hypot(nx, ny, nz) || 1;
    nx /= length; ny /= length; nz /= length;
    let tint = this.tints.get(hex);
    if (!tint) { tint = Color3.FromHexString(hex).toLinearSpace(); this.tints.set(hex, tint); }
    const repeat = this.metres;
    const points = [a, b, c];
    for (let i = 0; i < points.length; i++) {
      const p = points[i]!;
      this.positions.push(...p);
      this.normals.push(nx, ny, nz);
      this.colors.push(tint.r, tint.g, tint.b, 1);
      if (uv) this.uvs.push(uv[i]![0] / repeat, uv[i]![1] / repeat);
      // Match the terrain's signed sandstone projections at every escarpment join.
      else if (Math.abs(ny) > 0.65) this.uvs.push(p[0] * Math.sign(ny) / repeat, p[2] / repeat);
      else if (Math.abs(nx) > Math.abs(nz)) this.uvs.push(p[2] * Math.sign(nx) / repeat, p[1] / repeat);
      else this.uvs.push(-p[0] * Math.sign(nz) / repeat, p[1] / repeat);
    }
    this.indices.push(base, base + 2, base + 1);
  }
  quad(a: Point, b: Point, c: Point, d: Point, hex: string, uv?: readonly [UV, UV, UV, UV]): void {
    this.triangle(a, b, c, hex, uv && [uv[0], uv[1], uv[2]]);
    this.triangle(a, c, d, hex, uv && [uv[0], uv[2], uv[3]]);
  }
  box(x: number, y: number, z: number, w: number, h: number, d: number, hex: string, yaw = 0): void {
    const cs = Math.cos(yaw), sn = Math.sin(yaw);
    const point = (px: number, py: number, pz: number): Point => [x + px * cs + pz * sn, y + py, z - px * sn + pz * cs];
    const half: Point = [w / 2, h / 2, d / 2];
    const bevel = this.family === "wood" || this.family === "planks" ? Math.min(0.035, w * 0.075, h * 0.075, d * 0.075) : 0;
    const inner: Point = [half[0] - bevel, half[1] - bevel, half[2] - bevel];
    const vertex = (axis: number, a: number, u: number, b: number, v: number, c: number): Point =>
      point(axis === 0 ? a : u === 0 ? b : v === 0 ? c : 0, axis === 1 ? a : u === 1 ? b : v === 1 ? c : 0,
        axis === 2 ? a : u === 2 ? b : v === 2 ? c : 0);
    const grain: Point = h >= Math.max(w, d) ? [0, 1, 0] : w >= d ? [cs, 0, -sn] : [sn, 0, cs];
    const face = (p: Point, q: Point, r: Point, s?: Point): void => {
      const last = s ?? r;
      const ex = q[0] - p[0], ey = q[1] - p[1], ez = q[2] - p[2];
      const fx = last[0] - p[0], fy = last[1] - p[1], fz = last[2] - p[2];
      const nx = ey * fz - ez * fy, ny = ez * fx - ex * fz, nz = ex * fy - ey * fx;
      let tx = ny * grain[2] - nz * grain[1], ty = nz * grain[0] - nx * grain[2], tz = nx * grain[1] - ny * grain[0];
      const span = Math.hypot(tx, ty, tz);
      let longitudinal = grain;
      if (span < 0.001) {
        const edge = Math.hypot(ex, ey, ez);
        longitudinal = [ex / edge, ey / edge, ez / edge];
        const side = Math.hypot(fx, fy, fz);
        tx = fx / side; ty = fy / side; tz = fz / side;
      } else { tx /= span; ty /= span; tz /= span; }
      const uv = (v: Point): UV => {
        const px = v[0] - x, py = v[1] - y, pz = v[2] - z;
        return [px * tx + py * ty + pz * tz, px * longitudinal[0] + py * longitudinal[1] + pz * longitudinal[2]];
      };
      const outward = nx * (p[0] - x) + ny * (p[1] - y) + nz * (p[2] - z) >= 0;
      if (s) {
        if (outward) this.quad(p, q, r, s, hex, [uv(p), uv(q), uv(r), uv(s)]);
        else this.quad(p, s, r, q, hex, [uv(p), uv(s), uv(r), uv(q)]);
      } else if (outward) this.triangle(p, q, r, hex, [uv(p), uv(q), uv(r)]);
      else this.triangle(p, r, q, hex, [uv(p), uv(r), uv(q)]);
    };
    for (let axis = 0; axis < 3; axis++) {
      const u = (axis + 1) % 3, v = (axis + 2) % 3;
      for (const side of [-1, 1]) {
        face(vertex(axis, side * half[axis]!, u, -inner[u]!, v, -inner[v]!),
          vertex(axis, side * half[axis]!, u, inner[u]!, v, -inner[v]!),
          vertex(axis, side * half[axis]!, u, inner[u]!, v, inner[v]!),
          vertex(axis, side * half[axis]!, u, -inner[u]!, v, inner[v]!));
      }
      if (!bevel) continue;
      for (const su of [-1, 1]) for (const sv of [-1, 1]) {
        face(vertex(axis, -inner[axis]!, u, su * half[u]!, v, sv * inner[v]!),
          vertex(axis, inner[axis]!, u, su * half[u]!, v, sv * inner[v]!),
          vertex(axis, inner[axis]!, u, su * inner[u]!, v, sv * half[v]!),
          vertex(axis, -inner[axis]!, u, su * inner[u]!, v, sv * half[v]!));
      }
    }
    if (bevel) for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of [-1, 1])
      face(point(sx * half[0], sy * inner[1], sz * inner[2]),
        point(sx * inner[0], sy * half[1], sz * inner[2]), point(sx * inner[0], sy * inner[1], sz * half[2]));
  }
  log(a: Point, b: Point, radius: number, hex: string, top = radius * 0.72, sides = 7, caps = true): void {
    const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
    const length = Math.hypot(dx, dy, dz) || 1, ax = dx / length, ay = dy / length, az = dz / length;
    const flat = Math.hypot(ax, az);
    const ux = flat > 0.01 ? az / flat : 1, uz = flat > 0.01 ? -ax / flat : 0;
    const vx = ay * uz, vy = az * ux - ax * uz, vz = -ay * ux;
    const ring = (p: Point, r: number, angle: number): Point => [p[0] + r * (ux * Math.cos(angle) + vx * Math.sin(angle)), p[1] + r * vy * Math.sin(angle), p[2] + r * (uz * Math.cos(angle) + vz * Math.sin(angle))];
    for (let i = 0; i < sides; i++) {
      const t = i * Math.PI * 2 / sides, s = (i + 1) * Math.PI * 2 / sides;
      const p = ring(a, radius, t), q = ring(a, radius, s), r = ring(b, top, s), u = ring(b, top, t);
      this.quad(p, q, r, u, hex, [[t * radius, 0], [s * radius, 0], [s * radius, length], [t * radius, length]]);
      if (caps) {
        this.triangle(a, q, p, hex, [[0, 0], [radius * Math.cos(s), radius * Math.sin(s)], [radius * Math.cos(t), radius * Math.sin(t)]]);
        this.triangle(b, u, r, hex, [[0, 0], [top * Math.cos(t), top * Math.sin(t)], [top * Math.cos(s), top * Math.sin(s)]]);
      }
    }
  }
}

/** One fort mesh: a 40 m region's surface family, as shadow-casting structure or unshadowed detail. */
export interface SkullFortPart {
  readonly key: string;
  readonly family: SurfaceFamily;
  readonly shadows: boolean;
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  readonly colors: Float32Array;
  readonly uvs: Float32Array;
  /** 16-bit whenever every index fits, as Babylon uploads a plain index array. */
  readonly indices: Uint16Array | Uint32Array;
}

/** Every non-empty fort part in construction order; `metres` is each family's texture repeat. */
export function buildSkullFort(metres: Readonly<Record<SurfaceFamily, number>>): SkullFortPart[] {
  const parts: SkullFortPart[] = [];
  for (const [key, batch] of new FortBuilder(metres).batches) {
    if (!batch.indices.length) continue;
    parts.push({
      key, family: batch.family, shadows: batch.shadows,
      positions: Float32Array.from(batch.positions), normals: Float32Array.from(batch.normals),
      colors: Float32Array.from(batch.colors), uvs: Float32Array.from(batch.uvs),
      indices: batch.indices.some((index) => index >= 65535) ? Uint32Array.from(batch.indices) : Uint16Array.from(batch.indices),
    });
  }
  return parts;
}

/** Skull's static salvage fort. Every blocking structure is sourced from shared solids. */
class FortBuilder {
  readonly batches = new Map<string, FortBatch>();
  private readonly surfaceHeight = (x: number, z: number): number => terrainSurfaceHeight(SKULL_ISLE, x, z);
  constructor(private readonly metres: Readonly<Record<SurfaceFamily, number>>) {
    this.structures();
    this.towers();
    this.bridgeDetails();
    this.mast();
    this.cliffFaces();
    this.vegetation();
    this.salvageClusters();
    this.lanterns();
  }
  private batch(x: number, z: number, family: SurfaceFamily, shadows = true): FortBatch {
    const key = `${Math.floor((x - SKULL_ORIGIN.x) / 40)}:${Math.floor((z - SKULL_ORIGIN.z) / 40)}:${family}:${shadows ? "structure" : "detail"}`;
    let batch = this.batches.get(key);
    if (!batch) { batch = new FortBatch(family, shadows, this.metres[family]); this.batches.set(key, batch); }
    return batch;
  }
  private wood(seed: number): string { return WOOD[Math.floor(hash2(seed, 17, 521) * WOOD.length)]!; }
  private log(a: Point, b: Point, r: number, hex: string, family: SurfaceFamily = "wood", shadows = true, top = r * 0.72): void {
    const tiny = r < 0.065;
    this.batch(a[0], a[2], family, shadows).log(a, b, r, hex, top, tiny ? 3 : 7, !tiny);
  }
  private rope(a: Point, b: Point, r = 0.045): void { this.log(a, b, r, "#bba77d", "rope", false, r); }
  private box(x: number, y: number, z: number, w: number, h: number, d: number, hex: string, family: SurfaceFamily = "wood", yaw = 0, shadows = true): void {
    this.batch(x, z, family, shadows).box(x, y, z, w, h, d, hex, yaw);
  }
  private lash(x: number, y: number, z: number, radius: number): void {
    // Hexagonal rope chords must clear the timber, not just their corner points.
    radius = (radius + 0.028) / Math.cos(Math.PI / 6);
    for (let i = 0; i < 2; i++) {
      const yy = y + i * 0.09;
      for (let j = 0; j < 6; j++) {
        const a = j * Math.PI / 3, b = (j + 1) * Math.PI / 3;
        this.rope([x + radius * Math.cos(a), yy, z + radius * Math.sin(a)], [x + radius * Math.cos(b), yy, z + radius * Math.sin(b)], 0.028);
      }
    }
  }
  private structures(): void {
    for (let index = 0; index < SKULL_SOLIDS.length; index++) {
      const solid = SKULL_SOLIDS[index]!, c = solid.collider;
      // Shared segmented cylinders describe these inclined feet; towers() draws their continuous timber.
      if (solid.id.includes("-splayed-")) continue;
      // Bridge poles are already shared cylinders; its sole rope pass is bridgeDetails().
      if (/^(?:cliff-bridge|watch-boardwalk)-rail-/.test(solid.id)) continue;
      if (c.kind === "cyl") {
        this.log([c.x, c.y0, c.z], [c.x, c.y1, c.z], c.r, solid.material === "iron" ? "#4b4941" : this.wood(index), solid.material === "iron" ? "iron" : "wood", true, c.r);
        continue;
      }
      const x = (c.minX + c.maxX) / 2, z = (c.minZ + c.maxZ) / 2;
      const w = c.maxX - c.minX, d = c.maxZ - c.minZ, h = c.y1 - c.y0;
      const alongX = w > d, length = Math.max(w, d);
      const stairRail = solid.id.match(/-stair-rail-(\d+)-/);
      if (stairRail) {
        for (const yy of [c.y0 + 0.18, c.y1 - 0.08])
          this.rope([c.minX, yy - 0.125, z], [c.maxX, yy + 0.125, z]);
        // One support per two metres, not two coincident posts at every tread join.
        if (Number(stairRail[1]) % 4 === 0)
          this.log([c.minX, c.y0 - 0.125, z], [c.minX, c.y1 + 0.1, z], 0.085, this.wood(index));
        continue;
      }
      if (/palisade|wall/.test(solid.id)) {
        const n = Math.ceil(length / 0.46), thick = Math.min(w, d);
        for (let i = 0; i < n; i++) {
          const t = (i + 0.5) / n - 0.5, px = x + (alongX ? t * w : 0), pz = z + (alongX ? 0 : t * d);
          const rise = 0.25 + hash2(i, index, 601) * 1.2;
          const radius = Math.min(0.25, thick / 2), tint = this.wood(index * 37 + i);
          const lean = (hash2(i, index, 602) - 0.5) * 0.16;
          const shoulder: Point = [px + lean * 0.75, c.y1 - 0.5 + rise * 0.35, pz];
          // Full trunks form the wall; only the chipped crowns taper to points.
          this.log([px, c.y0, pz], shoulder, radius, tint, "wood", true, radius * 0.92);
          this.log(shoulder, [px + lean, c.y1 - 0.3 + rise, pz], radius * 0.92, tint, "wood", true, 0.025);
          if (i % 4 === 0) this.lash(px, c.y1 - 1.1, pz, Math.min(0.26, thick / 2 + 0.035));
          if (i % 9 === 0) this.box(px, c.y1 - 1.55, pz, 0.11, 1.5, 0.13, "#c1aa80", "wood", 0.12, false);
        }
        for (const yy of [c.y0 + h * 0.28, c.y0 + h * 0.66])
          this.box(x, yy, z, alongX ? w : Math.min(w, 0.18), 0.16, alongX ? Math.min(d, 0.18) : d, "#594331");
        // Salvaged diagonal battens sit on the wall face, never across an entrance.
        const braces = Math.floor(length / 4);
        for (let i = 0; i < braces; i++) {
          const low = -length / 2 + (i + 0.16) * length / braces;
          const high = -length / 2 + (i + 0.86) * length / braces;
          const face = Math.min(w, d) / 2 + 0.065;
          this.log([x + (alongX ? low : -face), c.y0 + h * 0.17, z + (alongX ? -face : low)],
            [x + (alongX ? high : -face), c.y0 + h * 0.79, z + (alongX ? -face : high)],
            0.055, this.wood(index + i * 11), "wood", true, 0.052);
        }
      } else if (/guard|rail/.test(solid.id)) {
        for (const yy of [c.y0 + 0.18, c.y1 - 0.08])
          this.box(x, yy, z, w, 0.13, d, solid.material === "rope" ? "#bcaa83" : "#8e7755", solid.material === "rope" ? "rope" : "wood", 0, solid.material !== "rope");
        const count = Math.ceil(length / 1.3);
        for (let i = 0; i <= count; i++) {
          const offset = (i / count - 0.5) * length;
          this.log([x + (alongX ? offset : 0), c.y0, z + (alongX ? 0 : offset)], [x + (alongX ? offset : 0), c.y1, z + (alongX ? 0 : offset)], Math.min(0.11, Math.min(w, d) / 2), this.wood(i + index));
        }
        if (/(?:watch|bastion)-rail/.test(solid.id)) {
          const boards = Math.ceil(length / 0.42);
          for (let i = 0; i < boards; i++) {
            const offset = ((i + 0.5) / boards - 0.5) * length;
            const height = h * (0.72 + hash2(i, index, 607) * 0.26);
            this.box(x + (alongX ? offset : 0), c.y0 + height / 2, z + (alongX ? 0 : offset),
              alongX ? length / boards - 0.035 : w, height, alongX ? d : length / boards - 0.035, this.wood(index + i));
          }
        }
      } else if (solid.material === "plank") {
        // An unbroken support envelope under narrow board seams, exactly at shared foot height.
        this.box(x, c.y0 + h * 0.2, z, w, h * 0.4, d, "#67513c", "planks");
        if (solid.id === "stores-floor")
          this.box(x, c.y0 - 0.25, z, w, 0.5, d, "#887452", "wood");
        const count = Math.max(1, Math.ceil((alongX ? w : d) / 0.48));
        for (let i = 0; i < count; i++) {
          const offset = (i + 0.5) / count - 0.5;
          this.box(x + (alongX ? offset * w : 0), c.y0 + h * 0.7, z + (alongX ? 0 : offset * d), alongX ? w / count - 0.025 : w, h * 0.6, alongX ? d : d / count - 0.025, this.wood(index + i * 13), "planks");
          if (i % 3 === 0) for (const side of [-1, 1])
            this.box(x + (alongX ? offset * w : side * w * 0.4), c.y1 + 0.005, z + (alongX ? side * d * 0.4 : offset * d), 0.035, 0.01, 0.035, "#4b4941", "iron", 0, false);
        }
      } else if (solid.id === "stores-crate") {
        this.crate(x, c.y0, z, w, index, d, h, 0);
      } else {
        // Tall structural members use their exact authoring footprint, dressed with grain and bands.
        this.box(x, (c.y0 + c.y1) / 2, z, w, h, d, this.wood(index), solid.material === "iron" ? "iron" : "wood");
        if (w < 1.5 && d < 1.5) this.lash(x, c.y1 - 0.35, z, Math.max(w, d) * 0.55);
      }
    }
  }
  private towers(): void {
    for (const [index, t] of SKULL_TOWERS.entries()) {
      const offshore = t.y < 0;
      const roofY = t.y + t.height;
      const halfW = t.width / 2, halfD = t.depth / 2;
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
        const px = t.x + sx * (halfW - 0.3), pz = t.z + sz * (halfD - 0.3);
        if (offshore) this.log([t.x + sx * 7.4, t.y, t.z + sz * 7.4], [px, t.deck - 0.3, pz], 0.4, this.wood(index + sx * 5 + sz), "wood", true, 0.4);
        this.log([px, roofY, pz], [px + sx * 0.12, roofY + 0.8 + hash2(sx, sz, 251 + index), pz], 0.2, this.wood(index + sx * 5 + sz), "wood", true, 0.04);
        this.lash(px, t.deck - 0.5, pz, 0.34);
      }
      // Four exposed scaffold frames, rather than solid tower-shaped boxes.
      for (let yy = Math.max(t.y + 2, t.deck - 9); yy < roofY - 1; yy += 3.3) {
        for (const side of [-1, 1]) {
          const z = t.z + side * (halfD - 0.3), x = t.x + side * (halfW - 0.3);
          this.log([t.x - halfW + 0.6, yy, z + side * 0.32], [t.x + halfW - 0.6, yy + 3.1, z + side * 0.32], 0.105, "#87714f");
          // Western faces are the actual stair entrances: never brace across carrying headroom.
          if (side > 0 || yy + 3.1 < t.deck - 0.1 || yy > t.deck + 2.7)
            this.log([x + side * 0.32, yy, t.z - halfD + 0.6], [x + side * 0.32, yy + 3.1, t.z + halfD - 0.6], 0.105, "#8b7554");
          this.log([t.x - halfW + 0.3, yy, z + side * 0.32], [t.x + halfW - 0.3, yy, z + side * 0.32], 0.12, "#a08962");
        }
      }
      for (const side of [-1, 1]) {
        this.log([t.x - halfW, roofY, t.z + side * halfD], [t.x + halfW, roofY, t.z + side * halfD], 0.15, "#af966d");
        for (let i = 0; i <= Math.ceil(t.width / 0.5); i++) {
          const x = t.x - halfW + i * 0.5;
          this.log([x, roofY - 0.25, t.z + side * halfD], [x + 0.1, roofY + 0.8 + hash2(i, index, 771) * 0.9, t.z + side * halfD], 0.1, this.wood(i), "wood", true, 0.01);
        }
      }
      this.banner(t.x + halfW + 0.3, t.deck + 3.5, t.z - halfD, offshore ? 3.8 : 2.1, offshore ? 5 : 2.6, 0.4);
      if (offshore) {
        this.banner(t.x - 1, t.deck - 0.4, t.z - halfD - 0.3, 4.5, 4.5, 0);
        this.rope([t.x - halfW, roofY, t.z - halfD], [t.x + halfW, roofY - 1.5, t.z - halfD]);
        this.log([t.x + 1, roofY, t.z], [t.x + 1, roofY + 6.5, t.z], 0.14, "#b69d73");
        this.banner(t.x + 1, roofY + 5.8, t.z, 2.5, 2, 0.2);
      }
    }
  }
  private bridgeDetails(): void {
    for (const path of SKULL_PATHS) {
      if (path.kind !== "bridge") continue;
      const a = path.points[0]!, b = path.points[1]!;
      for (const side of [-1, 1]) {
        const z = a.z + side * (path.width / 2 - 0.22);
        this.log([a.x, a.y - 0.54, z], [b.x, b.y - 0.54, z], 0.18, "#69523b", "wood", true, 0.18);
        for (const [x, direction] of [[a.x + 1, 1], [b.x - 1, -1]]) {
          // Braces stay at the abutments; the low return passes beneath the open middle span.
          const base = path.id === "cliff-bridge" ? 8 : -2;
          this.log([x!, base, z], [x! + direction! * 4, a.y - 0.4, z], 0.16, "#9f855c");
          this.lash(x!, a.y - 0.8, z, 0.28);
        }
        for (let x = a.x; x < b.x; x += 6) {
          const end = Math.min(b.x, x + 6), count = 5;
          for (let i = 0; i < count; i++) {
            const t = i / count, u = (i + 1) / count;
            this.rope([x + (end - x) * t, a.y + 1 - Math.sin(t * Math.PI) * 0.15, z + side * 0.35], [x + (end - x) * u, a.y + 1 - Math.sin(u * Math.PI) * 0.15, z + side * 0.35], 0.04);
          }
        }
      }
      if (path.id === "cliff-bridge") this.banner(a.x + 19, a.y - 0.5, a.z - path.width / 2 - 0.15, 4.8, 3.3, 0);
    }
  }
  private banner(x: number, y: number, z: number, w: number, h: number, yaw: number): void {
    const batch = this.batch(x, z, "fabric", true), cs = Math.cos(yaw), sn = Math.sin(yaw);
    const p = (u: number, v: number): Point => {
      const billow = Math.sin(u * Math.PI) * Math.sin(v * 2.3) * Math.min(w, 4) * 0.13;
      const hem = 1 - 0.09 * Math.max(0, Math.sin(u * 31 + w)) - 0.045 * Math.sin(u * 53) ** 2;
      const folds = Math.sin(u * 23 + v * 3) * Math.sin(u * Math.PI) * v * 0.045;
      return [x + u * w * cs + (billow + folds) * sn, y - v * h * hem,
        z - u * w * sn + (billow + folds) * cs];
    };
    for (let i = 0; i < 16; i++) for (let j = 0; j < 10; j++) {
      const u = i / 16, v = j / 10, nextU = (i + 1) / 16, nextV = (j + 1) / 10;
      const a = p(u, v), b = p(nextU, v), c = p(nextU, nextV), d = p(u, nextV);
      const tint = j > 7 ? "#c9ba99" : "#e3d4b2";
      batch.quad(a, b, c, d, tint, [[u * w, v * h], [nextU * w, v * h], [nextU * w, nextV * h], [u * w, nextV * h]]);
      batch.quad(d, c, b, a, tint, [[u * w, nextV * h], [nextU * w, nextV * h], [nextU * w, v * h], [u * w, v * h]]);
    }
    this.log([x - 0.6 * cs, y + 0.06, z + 0.6 * sn], [x + w * cs, y + 0.06, z - w * sn],
      0.032, "#9c825c", "wood", false, 0.028);
  }
  private mast(): void {
    const solid = SKULL_SOLIDS.find(s => s.id === "signal-mast")!;
    const c = solid.collider;
    if (c.kind !== "cyl") throw new Error("Skull signal mast must have its authored cylindrical envelope");
    const { x, z } = c, y = 34;
    // The full shared envelope is already rendered; only the salvaged tip extends it.
    this.log([x, c.y1, z], [x + 0.2, c.y1 + 1.4, z], c.r, "#967c55", "wood", true, 0.06);
    for (let i = 0; i < 5; i++) this.lash(x, y + 2 + i * 5, z, c.r + 0.055);
    for (const level of [12, 21, 27]) {
      const span = level === 12 ? 7 : level === 21 ? 5 : 2.7;
      this.log([x - span, y + level, z], [x + span, y + level + 0.2, z], 0.16, "#ae936b");
      this.rope([x - span, y + level, z], [x, y + level + 4, z]);
      this.rope([x + span, y + level + 0.2, z], [x, y + level + 4, z]);
    }
    for (const solid of SKULL_SOLIDS) {
      if (!solid.id.startsWith("mast-rigging-anchor-") || solid.collider.kind !== "box") continue;
      const anchor = solid.collider, ax = (anchor.minX + anchor.maxX) / 2, az = (anchor.minZ + anchor.maxZ) / 2;
      const left: Point = [ax, anchor.y1 - 0.15, az - 0.46], right: Point = [ax, anchor.y1 - 0.15, az + 0.46];
      const headLeft: Point = [x, y + 26, z - 0.35], headRight: Point = [x, y + 26, z + 0.35];
      this.rope(left, headLeft, 0.055); this.rope(right, headRight, 0.055);
      // Every ratline is tied to both continuous shrouds and the shared ground anchor.
      for (let j = 1; j < 31; j++) this.rope(lerp(left, headLeft, j / 32), lerp(right, headRight, j / 32), 0.025);
    }
    for (let i = 0; i < 12; i++) {
      const a = i * Math.PI / 6, b = (i + 1) * Math.PI / 6;
      const inner = c.r + 0.055;
      this.batch(x, z, "planks").quad(
        [x + Math.cos(a) * inner, y + 19.5, z + Math.sin(a) * inner],
        [x + Math.cos(b) * inner, y + 19.5, z + Math.sin(b) * inner],
        [x + Math.cos(b) * 1.6, y + 19.5, z + Math.sin(b) * 1.6],
        [x + Math.cos(a) * 1.6, y + 19.5, z + Math.sin(a) * 1.6], this.wood(i),
        [[0, 0], [inner * Math.PI / 6, 0], [1.6 * Math.PI / 6, 1.6 - inner], [0, 1.6 - inner]]);
      const planks = this.batch(x, z, "planks"), topY = y + 19.5, bottomY = topY - 0.16;
      const ia: Point = [x + Math.cos(a) * inner, topY, z + Math.sin(a) * inner];
      const ib: Point = [x + Math.cos(b) * inner, topY, z + Math.sin(b) * inner];
      const oa: Point = [x + Math.cos(a) * 1.6, topY, z + Math.sin(a) * 1.6];
      const ob: Point = [x + Math.cos(b) * 1.6, topY, z + Math.sin(b) * 1.6];
      const lower = (p: Point): Point => [p[0], bottomY, p[2]];
      planks.quad(lower(ia), lower(ib), lower(ob), lower(oa), "#806b4b");
      planks.quad(oa, ob, lower(ob), lower(oa), "#9c825c");
      planks.quad(ib, ia, lower(ia), lower(ib), "#806b4b");
      if (i % 3 === 0) this.log([x + Math.cos(a) * c.r, topY - 2, z + Math.sin(a) * c.r],
        lower(oa), 0.08, "#927953", "wood", true, 0.07);
      this.log([x + Math.cos(a) * 1.6, y + 19.4, z + Math.sin(a) * 1.6], [x + Math.cos(a) * 1.6, y + 21, z + Math.sin(a) * 1.6], 0.08, "#7c6247");
      this.rope([x + Math.cos(a) * 1.6, y + 20.8, z + Math.sin(a) * 1.6], [x + Math.cos(b) * 1.6, y + 20.8, z + Math.sin(b) * 1.6]);
    }
    this.banner(x + 0.25, y + 29.5, z, 4.7, 3.1, 0.15);
    this.banner(x - 4.8, y + 21, z, 3, 6.4, -0.08);
  }
  private clear(x: number, z: number, margin = 0, structures = true, lowY = -Infinity, highY = Infinity): boolean {
    for (const path of SKULL_PATHS) for (let i = 1; i < path.points.length; i++) {
      const a = path.points[i - 1]!, b = path.points[i]!, dx = b.x - a.x, dz = b.z - a.z;
      const length2 = dx * dx + dz * dz;
      if (length2 < 0.01) continue;
      const t = clamp(((x - a.x) * dx + (z - a.z) * dz) / length2, 0, 1);
      const feetY = a.y + (b.y - a.y) * t;
      if (lowY > feetY + 2.5 || highY < feetY - 0.5) continue;
      if ((x - a.x - dx * t) ** 2 + (z - a.z - dz * t) ** 2 < (path.width / 2 + 1 + margin) ** 2) return false;
    }
    for (const dig of SKULL_ISLE.digSpots) {
      const dx = SKULL_ORIGIN.x + dig.x, dz = SKULL_ORIGIN.z + dig.z;
      if ((x - dx) ** 2 + (z - dz) ** 2 >= (3 + margin) ** 2) continue;
      if (lowY !== -Infinity || highY !== Infinity) {
        const feetY = this.surfaceHeight(dx, dz);
        if (lowY > feetY + 2.5 || highY < feetY - 0.5) continue;
      }
      return false;
    }
    if (structures) for (const { collider: c } of SKULL_SOLIDS) if (this.overlaps(c, x, z, 0.5 + margin)) return false;
    if (structures) for (const palm of SKULL_PALMS)
      if ((x - SKULL_ORIGIN.x - palm.x) ** 2 + (z - SKULL_ORIGIN.z - palm.z) ** 2 < (0.55 + margin) ** 2) return false;
    return true;
  }
  private overlaps(c: Collider, x: number, z: number, margin: number): boolean {
    return c.kind === "cyl" ? (x - c.x) ** 2 + (z - c.z) ** 2 < (c.r + margin) ** 2
      : x > c.minX - margin && x < c.maxX + margin && z > c.minZ - margin && z < c.maxZ + margin;
  }
  private rock(x: number, y: number, z: number, w: number, h: number, d: number, seed: number, yaw = 0): void {
    const batch = this.batch(x, z, "rock", w > 1.5), bottom: Point[] = [], middle: Point[] = [], top: Point[] = [];
    const cs = Math.cos(yaw), sn = Math.sin(yaw);
    const rotated = (px: number, py: number, pz: number): Point => [x + px * cs - pz * sn, y + py, z + px * sn + pz * cs];
    for (let i = 0; i < 7; i++) {
      const angle = i * Math.PI * 2 / 7, irregular = 0.72 + hash2(i, seed, 443) * 0.35;
      bottom.push(rotated(Math.cos(angle) * w * 0.55, -h * 0.5, Math.sin(angle) * d * 0.55));
      middle.push(rotated(Math.cos(angle) * w * irregular * 0.55, h * (hash2(i, seed, 445) - 0.6) * 0.25, Math.sin(angle) * d * irregular * 0.55));
      top.push(rotated(Math.cos(angle) * w * irregular * 0.32, h * (0.34 + hash2(i, seed, 446) * 0.14), Math.sin(angle) * d * irregular * 0.38));
    }
    for (let i = 0; i < 7; i++) {
      const j = (i + 1) % 7, tint = ROCK[(i + seed) % ROCK.length]!;
      batch.quad(bottom[i]!, middle[i]!, middle[j]!, bottom[j]!, tint);
      batch.quad(middle[i]!, top[i]!, top[j]!, middle[j]!, tint);
      batch.triangle(top[j]!, top[i]!, [x, y + h * 0.39, z], ROCK[(seed + 2) % ROCK.length]!);
      // Undercuts are visible from the lower path: close the stone, not just its crest.
      batch.triangle(bottom[i]!, bottom[j]!, [x, y - h * 0.5, z], tint);
    }
  }
  private cliffFaces(): void {
    // The shared fractured terraces are the cliff solid. Only planted vines and ankle-scale
    // chips dress them: no duplicate stone shell, floating rows or render-only cliff walls.
    for (const [ti, terrace] of SKULL_TERRACES.entries()) {
      for (let i = 0; i < terrace.outline.length; i += 5) {
        const a = terrace.outline[i]!, b = terrace.outline[(i + 1) % terrace.outline.length]!;
        const dx = b.x - a.x, dz = b.z - a.z, span = Math.hypot(dx, dz);
        if (span < 0.01) continue;
        const nx = dz / span, nz = -dx / span;
        const x = SKULL_ORIGIN.x + a.x, z = SKULL_ORIGIN.z + a.z;
        const crest = this.surfaceHeight(x - nx * 2, z - nz * 2), foot = this.surfaceHeight(x + nx * 3, z + nz * 3);
        if (crest - foot > 4 && this.clear(x, z, 0.3, false))
          this.vine(x - nx * 1.6, crest - 1.5, z - nz * 1.6, 2.5 + hash2(i, ti, 616) * 2, i + ti * 71, nx, nz);
      }
    }
    for (let i = 0; i < 40; i++) {
      const x = SKULL_ORIGIN.x - 57 + hash2(i, 5, 615) * 62, z = SKULL_ORIGIN.z - 91 - hash2(i, 3, 616) * 12;
      if (!this.clear(x, z, 0.45)) continue;
      const y = this.surfaceHeight(x, z), w = 0.25 + hash2(i, 2, 617) * 0.45;
      if (y < -2 || y > 3 || Math.abs(this.surfaceHeight(x + w, z) - y) > 0.25) continue;
      this.rock(x, y - 0.025, z, w, 0.18 + hash2(i, 2, 618) * 0.12, w * 0.72, i + 400, i * 2.399);
    }
  }
  private leafBlade(root: Point, tip: Point, halfWidth: number, seed: number, shadows: boolean): void {
    const dx = tip[0] - root[0], dz = tip[2] - root[2], flat = Math.hypot(dx, dz);
    if (flat < 0.001) return;
    const nx = -dz / flat, nz = dx / flat;
    const length = Math.hypot(flat, tip[1] - root[1]), tint = LEAF[seed % LEAF.length]!;
    const batch = this.batch(root[0], root[2], "leaf", shadows);
    const section = (t: number): readonly [Point, Point, Point, number] => {
      const half = halfWidth * Math.sin(Math.PI * t) ** 0.8;
      const arch = Math.sin(Math.PI * t) * length * 0.11;
      const centre: Point = [root[0] + dx * t, root[1] + (tip[1] - root[1]) * t + arch, root[2] + dz * t];
      return [centre, [centre[0] + nx * half, centre[1] - half * 0.42, centre[2] + nz * half],
        [centre[0] - nx * half, centre[1] - half * 0.42, centre[2] - nz * half], half];
    };
    for (let i = 0; i < 6; i++) {
      const t = i / 6, u = (i + 1) / 6, a = section(t), b = section(u);
      for (const side of [1, 2] as const) {
        const sign = side === 1 ? 1 : -1;
        const uvA: UV = [a[3] * sign, t * length], uvB: UV = [b[3] * sign, u * length];
        if (i === 0) batch.triangle(a[0], b[0], b[side], tint, [[0, 0], [0, u * length], uvB]);
        else if (i === 5) batch.triangle(a[0], b[0], a[side], tint, [[0, t * length], [0, length], uvA]);
        else batch.quad(a[0], b[0], b[side], a[side], tint, [[0, t * length], [0, u * length], uvB, uvA]);
      }
    }
  }
  private palmFrond(root: Point, angle: number, length: number, width: number, rise: number, seed: number): void {
    const cs = Math.cos(angle), sn = Math.sin(angle);
    const axis = (t: number): Point => [
      root[0] + cs * length * t + sn * Math.sin(t * Math.PI) * length * 0.045,
      root[1] + Math.sin(t * Math.PI * 0.86) * rise - t * t * length * 0.36,
      root[2] + sn * length * t - cs * Math.sin(t * Math.PI) * length * 0.045,
    ];
    // A tapered, curved rachis connects every folded lanceolate leaflet.
    for (let i = 0; i < 14; i++) this.log(axis(i / 14), axis((i + 1) / 14),
      0.022 + (1 - i / 14) * 0.022, "#80924d", "leaf", false, 0.022 + (1 - (i + 1) / 14) * 0.022);
    for (let i = 0; i < 27; i++) for (const side of [-1, 1]) {
      const t = 0.055 + i * 0.0325 + (side > 0 ? 0.008 : 0);
      const base = axis(t), sweep = 0.17 + 0.18 * t;
      const span = width * 0.55 * Math.sin(Math.PI * t) ** 0.65 * (0.94 + hash2(i, seed, 459) * 0.12);
      const tip: Point = [base[0] - sn * span * side + cs * span * sweep,
        base[1] - span * (0.2 + t * 0.18), base[2] + cs * span * side + sn * span * sweep];
      this.leafBlade(base, tip, length / 27 * (0.43 + hash2(i, seed, 460) * 0.12), seed + i % 3, true);
    }
    this.leafBlade(axis(0.9), axis(1), length * 0.032, seed, true);
  }
  private frond(root: Point, angle: number, length: number, width: number, rise: number, seed: number): void {
    const cs = Math.cos(angle), sn = Math.sin(angle);
    this.leafBlade(root, [root[0] + cs * length, root[1] + rise * 0.5 - length * 0.12,
      root[2] + sn * length], width * 0.5, seed, false);
  }
  private palm(palm: SkullPalm): void {
    const { height, seed, bend } = palm;
    let travelled = 0;
    for (const { a, b, radius, topRadius } of palm.trunk) {
      const end = travelled + Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
      const batch = this.batch(a.x, a.z, "bark");
      for (let i = 0; i < 7; i++) {
        const t = i * Math.PI * 2 / 7, u = (i + 1) * Math.PI * 2 / 7;
        // Horizontal joint rings are identical on both segments: no internal caps or crossed seams.
        batch.quad(
          [a.x + Math.cos(t) * radius, a.y, a.z - Math.sin(t) * radius],
          [a.x + Math.cos(u) * radius, a.y, a.z - Math.sin(u) * radius],
          [b.x + Math.cos(u) * topRadius, b.y, b.z - Math.sin(u) * topRadius],
          [b.x + Math.cos(t) * topRadius, b.y, b.z - Math.sin(t) * topRadius], "#94805a",
          [[t * radius, travelled], [u * radius, travelled], [u * topRadius, end], [t * topRadius, end]]);
      }
      travelled = end;
    }
    const crown = palm.trunk[palm.trunk.length - 1]!.b, previous: Point = [crown.x, crown.y, crown.z];
    // Three overlapping age layers give a full tropical crown, not a star of thin cards.
    for (let layer = 0; layer < 3; layer++) {
      const count = layer === 2 ? 6 : 9;
      for (let i = 0; i < count; i++) {
        const angle = bend + i * Math.PI * 2 / count + layer * 0.43 + hash2(i, seed, 452) * 0.16;
        const length = height * (layer === 2 ? 0.36 : 0.53 + hash2(i, seed, 453) * 0.13);
        this.palmFrond([previous[0], previous[1] + layer * 0.14, previous[2]], angle, length,
          layer === 2 ? 1.9 : 2.6 + hash2(i, seed, 454) * 0.55, height * (0.08 + layer * 0.13), seed + i + layer * 9);
      }
    }
    this.log([previous[0], previous[1] - 0.24, previous[2]], [previous[0], previous[1] + 0.45, previous[2]],
      0.18, "#7f8d47", "leaf", true, 0.06);
    for (let i = 0; i < 5; i++) {
      const angle = i * Math.PI * 2 / 5 + bend;
      const fruit: Point = [previous[0] + Math.cos(angle) * 0.24, previous[1] - 0.35, previous[2] + Math.sin(angle) * 0.24];
      this.log(fruit, [fruit[0] * 0.25 + previous[0] * 0.75, previous[1] - 0.12, fruit[2] * 0.25 + previous[2] * 0.75],
        0.14, "#756038", "bark", false, 0.1);
    }
  }
  private fern(x: number, z: number, size: number, seed: number): void {
    const y = this.surfaceHeight(x, z) + 0.04;
    for (let i = 0; i < 9; i++) this.frond([x, y, z], i * 2.399 + seed, size, size * 0.58, size * 0.6, seed + i);
  }
  private vine(x: number, y: number, z: number, length: number, seed: number, nx: number, nz: number): void {
    for (let i = 0; i < 2; i++) {
      const startX = x - nz * i * 0.27, startZ = z + nx * i * 0.27;
      let previous: Point = [startX, this.surfaceHeight(startX, startZ) + 0.12, startZ];
      for (let j = 1; j <= Math.ceil(length / 0.8); j++) {
        const targetY = y - j * 0.8;
        let distance = 0;
        while (distance < 4 && this.surfaceHeight(startX + nx * distance, startZ + nz * distance) > targetY) distance += 0.12;
        if (distance >= 4) break;
        const px = startX + nx * distance, pz = startZ + nz * distance;
        const current: Point = [px, this.surfaceHeight(px, pz) + 0.12, pz];
        this.log(previous, current, 0.026, "#556d34", "leaf", false, 0.022);
        this.frond(current, seed + j * 2.4, 0.3, 0.2, 0.12, seed + j);
        previous = current;
      }
    }
  }
  private vegetation(): void {
    const seed = SKULL_PALMS.length;
    for (const palm of SKULL_PALMS) this.palm(palm);
    // Only low, nonblocking foliage may be culled separately by the scene.
    for (const [cx, cz, radius, count] of SKULL_GARDENS) {
      for (let i = 0; i < count!; i++) {
        const angle = i * 2.399 + cx!, r = radius! * Math.sqrt(hash2(i, cx!, 921));
        const x = SKULL_ORIGIN.x + cx! + Math.cos(angle) * r, z = SKULL_ORIGIN.z + cz! + Math.sin(angle) * r;
        const y = this.surfaceHeight(x, z), slope = Math.abs(this.surfaceHeight(x + 0.7, z) - y) + Math.abs(this.surfaceHeight(x, z + 0.7) - y);
        if (y < 3 || slope > 1.1 || !this.clear(x, z, 0.7)) continue;
        for (let j = 0; j < 6; j++) {
          const px = x + Math.cos(j * 2.399) * (1 + j * 0.35), pz = z + Math.sin(j * 2.399) * (1 + j * 0.35);
          if (this.clear(px, pz)) this.fern(px, pz, 0.9 + hash2(j, i, 933) * 1.6, seed + j);
        }
      }
    }
  }
  private crate(x: number, y: number, z: number, w: number, seed: number, depth = w * 0.8, height = w * 0.8, yaw = (hash2(seed, 5, 193) - 0.5) * 0.4): void {
    const cs = Math.cos(yaw), sn = Math.sin(yaw), band = Math.min(w, depth, height) * 0.085;
    const part = (px: number, py: number, pz: number, width: number, rise: number, length: number): void =>
      this.box(x + px * cs + pz * sn, y + py, z - px * sn + pz * cs, width, rise, length, "#a68e68", "wood", yaw, false);
    // Recess the panels within a joined frame; the complete crate retains its shared envelope.
    this.box(x, y + height / 2, z, w - 2 * band, height - 2 * band, depth - 2 * band, "#927953", "planks", yaw, false);
    for (let side = -1; side <= 1; side += 2) {
      for (let end = -1; end <= 1; end += 2)
        part(side * (w - band) / 2, height / 2, end * (depth - band) / 2, band, height, band);
      for (const yy of [band / 2, height - band / 2]) {
        part(0, yy, side * (depth - band) / 2, w - 2 * band, band, band);
        part(side * (w - band) / 2, yy, 0, band, band, depth - 2 * band);
      }
      const faceZ = side * (depth / 2 - band * 0.35);
      const a: Point = [x - (w / 2 - band) * cs + faceZ * sn, y + band, z + (w / 2 - band) * sn + faceZ * cs];
      const b: Point = [x + (w / 2 - band) * cs + faceZ * sn, y + height - band, z - (w / 2 - band) * sn + faceZ * cs];
      this.log(a, b, band * 0.38, "#a18a64", "wood", false, band * 0.38);
      for (const end of [a, b]) this.box(end[0], end[1], end[2] + side * band * 0.4, 0.025, 0.025, 0.025, "#4a4942", "iron", yaw, false);
    }
  }
  private barrel(x: number, y: number, z: number, seed: number): void {
    this.log([x, y, z], [x, y + 1.2, z], 0.43, this.wood(seed), "planks", false, 0.41);
    const iron = this.batch(x, z, "iron", false);
    const radius = (height: number): number => 0.43 - (height - y) * (0.02 / 1.2);
    const ring = (height: number, offset: number, i: number): Point => {
      const angle = i * Math.PI * 2 / 7, r = radius(height) + offset;
      return [x + Math.cos(angle) * r, height, z - Math.sin(angle) * r];
    };
    // Closed seven-sided strips follow the body's exact taper and face angles.
    // A twelve-sided tube ring cuts into the flats of a seven-sided body.
    for (const centre of [y + 0.16, y + 0.98]) {
      const low = centre - 0.052, high = centre + 0.052;
      for (let i = 0; i < 7; i++) {
        const j = i + 1;
        const a = ring(low, 0.022, i), b = ring(low, 0.022, j);
        const c = ring(high, 0.022, j), d = ring(high, 0.022, i);
        const e = ring(low, 0.001, i), f = ring(low, 0.001, j);
        const g = ring(high, 0.001, j), h = ring(high, 0.001, i);
        iron.quad(a, b, c, d, "#575248", [[0, 0], [0.37, 0], [0.37, 0.104], [0, 0.104]]);
        iron.quad(d, c, g, h, "#716959");
        iron.quad(e, f, b, a, "#47453e");
        iron.quad(h, g, f, e, "#575248");
      }
    }
    // Joined inset lid and bung stay inside the existing barrel envelope.
    this.box(x + 0.11, y + 1.205, z, 0.075, 0.018, 0.075, "#66533b", "wood", 0, false);
  }
  private canopy(x: number, z: number, size: number): void {
    const y = this.surfaceHeight(x, z);
    for (const side of [-1, 1]) {
      this.log([x + side * size, y, z], [x + side * size, y + 4.7, z], 0.12, "#9c825c");
      this.rope([x + side * size, y + 4.5, z], [x + side * (size + 2), this.surfaceHeight(x + side * (size + 2), z) + 0.1, z]);
    }
    const cloth = this.batch(x, z, "fabric");
    const roof = (u: number, v: number): Point => [
      x + (u - 0.5) * size * 2, y + 4.7 - Math.abs(v) * 1.9 - Math.sin(u * Math.PI) * 0.18
        + Math.sin(u * 27) * Math.abs(v) * 0.035, z + v * size * 0.8,
    ];
    for (let i = 0; i < 12; i++) for (let j = 0; j < 12; j++) {
      const u = i / 12, v = j / 6 - 1, nextU = (i + 1) / 12, nextV = (j + 1) / 6 - 1;
      const a = roof(u, v), b = roof(nextU, v), c = roof(nextU, nextV), d = roof(u, nextV);
      const uv: readonly [UV, UV, UV, UV] = [[u * size * 2, v * size], [nextU * size * 2, v * size],
        [nextU * size * 2, nextV * size], [u * size * 2, nextV * size]];
      cloth.quad(a, b, c, d, "#dccda9", uv);
      cloth.quad(d, c, b, a, "#dccda9", [uv[3], uv[2], uv[1], uv[0]]);
    }
    for (const side of [-1, 1]) {
      for (let i = 0; i < 12; i++) this.rope(roof(i / 12, side), roof((i + 1) / 12, side), 0.02);
      for (const end of [0, 1]) {
        const pegX = x + (end * 2 - 1) * (size + 1), pegZ = z + side * (size * 0.8 + 1);
        if (!this.clear(pegX, pegZ, 0.15)) continue;
        const ground = this.surfaceHeight(pegX, pegZ);
        this.log([pegX, ground - 0.05, pegZ], [pegX, ground + 0.15, pegZ], 0.035, "#8b7251", "wood", false, 0.02);
        this.rope(roof(end, side), [pegX, ground + 0.1, pegZ], 0.025);
      }
    }
  }
  private lanterns(): void {
    for (const { x, y, z, mountX, mountY, mountZ } of SKULL_LIGHT_ANCHORS) {
      const bracket: Point = [mountX, y + 0.92, mountZ];
      if (Math.abs(mountY - bracket[1]) > 0.02)
        this.log([mountX, mountY, mountZ], bracket, 0.055, "#67543e", "wood", false, 0.055);
      if (Math.hypot(x - mountX, z - mountZ) > 0.02)
        this.log(bracket, [x, y + 0.92, z], 0.055, "#67543e", "wood", false, 0.055);
      this.rope([x, y + 0.92, z], [x, y + 0.33, z], 0.021);
      this.box(x, y, z, 0.18, 0.36, 0.18, "#ffd891", "lamp", 0, false);
      for (const yy of [y - 0.23, y + 0.23])
        this.box(x, yy, z, 0.3, 0.065, 0.3, "#504c40", "iron", 0, false);
      for (const sx of [-1, 1]) for (const sz of [-1, 1])
        this.log([x + sx * 0.13, y - 0.2, z + sz * 0.13], [x + sx * 0.13, y + 0.2, z + sz * 0.13],
          0.019, "#504c40", "iron", false, 0.019);
      this.log([x, y + 0.26, z], [x, y + 0.34, z], 0.08, "#504c40", "iron", false, 0.03);
    }
  }
  private salvageClusters(): void {
    // Small objects are narrative clusters, not arbitrary scatter across the lawn.
    const clusters: readonly [number, number, number, "repair" | "stores" | "camp" | "battery"][] = [
      [-46, -94, 6, "repair"], [-20, -70, 7, "stores"], [-34, 4, 6, "stores"], [22, 34, 5.5, "camp"], [31, 56, 6, "battery"],
    ];
    const placed: { x: number; z: number; radius: number }[] = [];
    for (const [index, [lx, lz, size, kind]] of clusters.entries()) {
      const cx = SKULL_ORIGIN.x + lx, cz = SKULL_ORIGIN.z + lz;
      if (this.clear(cx, cz, size * 0.35) && (kind === "camp" || kind === "stores")) this.canopy(cx, cz, kind === "camp" ? 3.4 : 2.5);
      for (let i = 0; i < 13; i++) {
        const a = i * 2.399, r = 1.4 + hash2(i, index, 136) * size;
        const x = cx + Math.cos(a) * r, z = cz + Math.sin(a) * r, y = this.surfaceHeight(x, z);
        const footprint = i % 4 === 2 ? (kind === "repair" ? 2 : 1.3) : i % 4 === 1 ? 1 : 0.5;
        if (y < 1 || !this.clear(x, z, footprint) || Math.abs(this.surfaceHeight(x + footprint, z) - y) > 0.6
          || Math.abs(this.surfaceHeight(x, z + footprint) - y) > 0.6
          || placed.some(p => (p.x - x) ** 2 + (p.z - z) ** 2 < (p.radius + footprint + 0.15) ** 2)) continue;
        placed.push({ x, z, radius: footprint });
        if (i % 4 === 0) this.barrel(x, y, z, i + index);
        else if (i % 4 === 1) {
          const width = 0.85 + i % 3 * 0.22;
          this.crate(x, y, z, width, i);
          if (i % 3 === 0) this.crate(x + 0.08, y + width * 0.8, z, 0.7, i + 1);
        } else if (i % 4 === 2) {
          for (let j = 0; j < 5; j++) this.box(x + (j % 2) * 0.03, y + 0.06 + j * 0.12, z, 0.18, 0.12, kind === "repair" ? 3.8 : 2.3, this.wood(i + j), "wood", index * 0.6, false);
        } else {
          for (let j = 0; j < 4; j++) {
            const yy = y + 0.04;
            for (let k = 0; k < 14; k++) {
              const aa = k * Math.PI / 7, bb = (k + 1) * Math.PI / 7;
              this.rope([x + Math.cos(aa) * (0.4 - j * 0.075), yy, z + Math.sin(aa) * (0.4 - j * 0.075)], [x + Math.cos(bb) * (0.4 - j * 0.075), yy, z + Math.sin(bb) * (0.4 - j * 0.075)], 0.032);
            }
          }
        }
      }
      if (kind === "repair") this.boatFrame(cx - 3, cz + 2);
      if (kind === "battery") this.battery(cx, cz);
      if (kind === "camp") for (const side of [-1, 1]) {
        const x = cx + side * 2.5, z = cz + 2;
        if (this.clear(x, z, 0.7)) this.box(x, this.surfaceHeight(x, z) + 0.12, z, 0.85, 0.12, 2, side === 1 ? "#a19473" : "#787c5c", "fabric", 0.3, false);
      }
    }
    // Stock occupies the authored store bay, not the underbridge return lane.
    const storeFloor = SKULL_SOLIDS.find(s => s.id === "stores-floor")!.collider;
    if (storeFloor.kind === "box") {
      const x = storeFloor.minX + 1.2, y = storeFloor.y1, z = (storeFloor.minZ + storeFloor.maxZ) / 2;
      for (const offset of [-3.1, 3.1]) {
        this.barrel(x, y, z + offset, 611 + offset);
        this.crate(x + 1.15, y, z + offset + 0.3, 0.7, 616);
      }
      for (const yy of [y + 1.6, y + 2.6]) {
        this.box(x - 0.25, yy, z, 1.5, 0.12, 7.6, "#a38b62", "planks");
        for (let i = 0; i < 6; i++) this.log([x - 0.3, yy + 0.08, z - 3.1 + i * 1.2], [x - 0.3, yy + 0.5, z - 3.1 + i * 1.2], 0.18, i % 2 ? "#b8aa87" : "#74745a", "fabric", false, 0.1);
      }
    }
    const fireX = SKULL_ORIGIN.x + 28, fireZ = SKULL_ORIGIN.z + 31;
    if (this.clear(fireX, fireZ, 1)) {
      const y = this.surfaceHeight(fireX, fireZ);
      for (let i = 0; i < 9; i++) {
        const angle = i * Math.PI * 2 / 9;
        this.rock(fireX + Math.cos(angle) * 0.7, y + 0.08, fireZ + Math.sin(angle) * 0.7, 0.35, 0.22, 0.3, i);
      }
      for (let i = 0; i < 3; i++) this.box(fireX, y + 0.1, fireZ, 0.14, 0.12, 0.95, "#393529", "wood", i * 1.1, false);
      this.log([fireX, y + 0.25, fireZ], [fireX, y + 0.57, fireZ], 0.3, "#49473d", "iron", false, 0.34);
    }
  }
  private boatFrame(x: number, z: number): void {
    if (!this.clear(x, z, 2)) return;
    const y = this.surfaceHeight(x, z) + 0.2;
    this.log([x, y, z - 3.5], [x, y, z + 3.5], 0.12, "#70543a");
    for (const offset of [-1.8, 1.8]) {
      const yy = this.surfaceHeight(x, z + offset);
      this.box(x, yy + 0.08, z + offset, 1.8, 0.16, 0.2, "#766044", "wood", 0, false);
    }
    for (let i = 0; i < 9; i++) {
      const zz = z - 3.1 + i * 0.75, w = Math.sin((i + 1) * Math.PI / 10) * 1.4;
      for (const side of [-1, 1]) {
        this.log([x, y, zz], [x + side * w * 0.7, y + 0.35, zz], 0.08, "#b59b72", "wood", false);
        this.log([x + side * w * 0.7, y + 0.35, zz], [x + side * w, y + 1.1, zz], 0.08, "#9d7d52", "wood", false);
        if (i < 8) {
          const nextW = Math.sin((i + 2) * Math.PI / 10) * 1.4;
          this.log([x + side * w, y + 0.65, zz], [x + side * nextW, y + 0.65, zz + 0.75], 0.06, "#8c7654", "planks", false, 0.06);
        }
      }
    }
  }
  private battery(x: number, z: number): void {
    for (const side of [-1, 1]) {
      const px = x + side * 2.3, pz = z + 1;
      if (!this.clear(px, pz, 1.4)) continue;
      const y = this.surfaceHeight(px, pz);
      this.box(px, y + 0.4, pz, 1.4, 0.45, 2.1, "#67533c", "wood", 0.2);
      for (const sx of [-1, 1]) {
        this.box(px + sx * 0.35, y + 0.8, pz - 0.25, 0.18, 0.65, 1.15, "#8c704d", "wood", 0.2);
        for (const end of [-0.55, 0.55])
          this.log([px + sx * 0.63, y + 0.35, pz + end], [px + sx * 0.88, y + 0.35, pz + end],
            0.34, "#766044", "wood", false, 0.34);
      }
      const breech: Point = [px, y + 1.05, pz - 1], muzzle: Point = [px + 0.5, y + 1.4, pz + 1.5];
      const metal = this.batch(px, pz, "iron"), dx = muzzle[0] - breech[0], dy = muzzle[1] - breech[1], dz = muzzle[2] - breech[2];
      const span = Math.hypot(dx, dy, dz), flat = Math.hypot(dx, dz), ux = dz / flat, uz = -dx / flat;
      const vx = dy / span * uz, vy = dz / span * ux - dx / span * uz, vz = -dy / span * ux;
      const ring = (p: Point, radius: number, angle: number): Point => [p[0] + radius * (ux * Math.cos(angle) + vx * Math.sin(angle)),
        p[1] + radius * vy * Math.sin(angle), p[2] + radius * (uz * Math.cos(angle) + vz * Math.sin(angle))];
      metal.log(breech, muzzle, 0.25, "#494840", 0.2, 9, false);
      const recess = lerp(breech, muzzle, 0.78);
      for (let i = 0; i < 9; i++) {
        const a = i * Math.PI * 2 / 9, b = (i + 1) * Math.PI * 2 / 9;
        const outerA = ring(muzzle, 0.2, a), outerB = ring(muzzle, 0.2, b);
        const innerA = ring(muzzle, 0.115, a), innerB = ring(muzzle, 0.115, b);
        metal.quad(outerA, outerB, innerB, innerA, "#656050");
        metal.quad(innerA, innerB, ring(recess, 0.115, b), ring(recess, 0.115, a), "#272923");
        metal.triangle(recess, ring(recess, 0.115, a), ring(recess, 0.115, b), "#1e211d");
        metal.triangle(breech, ring(breech, 0.25, b), ring(breech, 0.25, a), "#494840");
      }
    }
  }
}
